import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import {
  LoyaltyProgramControllerService,
  LoyaltyProgramDto,
  TransactionControllerService,
  TransactionDto,
} from 'src/app/api-client';
import { TransactionFiltersV2Service } from 'src/app/core/api-v2/transaction-filters-v2.service';
import { ModalCustomComponent } from 'src/app/shared/components/modal-custom/modal-custom.component';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { UnreadTransactionsService } from 'src/app/shared/services/unread-transactions.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { environment } from 'src/environments/environment';
import {
  avatarColor,
  avatarInitials,
  croatianPluralSuffix,
  formatAmount,
  isLoyaltyEnded,
  priceToPointsRatio,
  relativeDateTime,
  statusChipClass,
  transactionKind,
  transactionKindIcon,
  isCouponKind,
} from './transaction-utils';

type StatusFilter = TransactionDto.StatusEnum | null;
type TypeFilter = 'INVOICE_SCAN' | 'COUPON_BOUGHT' | 'MANUAL_TRANSACTION' | null;
type PeriodFilter = 'TODAY' | 'LAST_7' | 'LAST_30' | null;

@Component({
  selector: 'app-transactions',
  standalone: true,
  imports: [CommonModule, SharedModule, MatPaginatorModule, MatMenuModule],
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss'],
})
export class TransactionsComponent implements OnInit, OnDestroy {
  private _transactions = inject(TransactionControllerService);

  // TODO: replace with the generated api-client service after client regeneration
  private _transactionFilters = inject(TransactionFiltersV2Service);

  private _loyaltyPrograms = inject(LoyaltyProgramControllerService);

  private _notification = inject(NotificationService);

  private _transloco = inject(TranslocoService);

  private _unreadTransactions = inject(UnreadTransactionsService);

  private _router = inject(Router);

  private _destroy$ = new Subject<void>();

  public transactionsData = signal<TransactionDto[]>(null);

  public backendUrl = environment.url;

  public totalElements = signal<number>(0);

  public pendingCount = signal<number>(0);

  public loading: boolean = false;

  public readonly pageSize: number = 10;

  public searchControl = new FormControl<string>('');

  public statusFilter = signal<StatusFilter>(null);

  public typeFilter = signal<TypeFilter>(null);

  public programFilter = signal<LoyaltyProgramDto | null>(null);

  public periodFilter = signal<PeriodFilter>(null);

  public programList = signal<LoyaltyProgramDto[]>([]);

  public periodList: Exclude<PeriodFilter, null>[] = ['TODAY', 'LAST_7', 'LAST_30'];

  public statusList: TransactionDto.StatusEnum[] = [
    'VALIDATION_PENDING',
    'SUCCESS',
    'DECLINED',
    'INITIALIZED',
    'ERROR',
    'REPORTED',
  ];

  public typeList: Exclude<TypeFilter, null>[] = [
    'INVOICE_SCAN',
    'COUPON_BOUGHT',
    'MANUAL_TRANSACTION',
  ];

  public selectedTransaction: TransactionDto = null;

  public cancelTransactionMessage: string = null;

  /** Show the Iznos column only when at least one row's ratio is known. */
  public showAmountColumn = computed(() =>
    (this.transactionsData() ?? []).some(
      (transaction) => priceToPointsRatio(transaction) !== null
    )
  );

  @ViewChild(MatPaginator) paginator: MatPaginator;

  @ViewChild('cancelTransactionModal') cancelTransactionModal: ModalCustomComponent;

  // Template helpers (shared with the detail page)
  public avatarColor = avatarColor;

  public avatarInitials = avatarInitials;

  public statusChipClass = statusChipClass;

  public transactionKind = transactionKind;

  public transactionKindIcon = transactionKindIcon;

  public isCouponKind = isCouponKind;

  public formatAmount = formatAmount;

  public isLoyaltyEnded = isLoyaltyEnded;

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe(() => this.resetPageAndFetch());
    this.getTransactions();
    this.refreshPendingCount();
    this.getLoyaltyPrograms();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /** Get transactions from db */
  public getTransactions() {
    this.loading = true;
    const { dateFrom, dateTo } = this.periodRange();
    this._transactionFilters
      .getTransactions(this.paginator?.pageIndex ?? 0, this.pageSize, {
        query: this.searchControl.value || undefined,
        status: this.statusFilter() ?? undefined,
        type: this.typeFilter() ?? undefined,
        loyaltyProgramId: this.programFilter()?.id ?? undefined,
        dateFrom,
        dateTo,
        direction: 'desc',
        sortBy: 'created',
      })
      .subscribe({
        next: (transactions) => {
          this.transactionsData.set(transactions.content ?? []);
          this.totalElements.set(transactions.totalElements ?? 0);
          if (this.paginator) {
            this.paginator.length = transactions.totalElements ?? 0;
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  /** Refresh both the list and the pending highlight/badge count. */
  public refresh() {
    this.getTransactions();
    this.refreshPendingCount();
  }

  /**
   * Refresh after a mutation that drops the row from the current view
   * (accept/cancel under an active filter). When the page held a single row
   * and we are past the first page, step back one page so the user is not
   * stranded on a now-empty page with the controls hidden.
   */
  private refreshAfterRowRemoved() {
    const onLastRow = (this.transactionsData()?.length ?? 0) <= 1;
    if (onLastRow && this.paginator && this.paginator.pageIndex > 0) {
      this.paginator.pageIndex = this.paginator.pageIndex - 1;
    }
    this.getTransactions();
    this.refreshPendingCount();
  }

  /**
   * Count VALIDATION_PENDING transactions (via totalElements of a 1-row page)
   * and keep the sidebar unread badge in sync with it.
   */
  public refreshPendingCount() {
    this._transactionFilters
      .getTransactions(0, 1, { status: 'VALIDATION_PENDING' })
      .subscribe({
        next: (page) => {
          const count = page.totalElements ?? 0;
          this.pendingCount.set(count);
          this._unreadTransactions.setUnreadTransactionsCount(count);
        },
      });
  }

  /** "N transakcija čeka…" headline with Croatian pluralisation. */
  public pendingTitle(): string {
    const count = this.pendingCount();
    const key = `transactions.pendingTitle${croatianPluralSuffix(count)}`;
    return this._transloco.translate(key, { count });
  }

  /** Pending-highlight CTA — apply the pending status filter. */
  public showPending() {
    this.statusFilter.set('VALIDATION_PENDING');
    this.resetPageAndFetch();
  }

  public setStatusFilter(status: StatusFilter) {
    this.statusFilter.set(status);
    this.resetPageAndFetch();
  }

  public setTypeFilter(type: TypeFilter) {
    this.typeFilter.set(type);
    this.resetPageAndFetch();
  }

  public setProgramFilter(program: LoyaltyProgramDto | null) {
    this.programFilter.set(program);
    this.resetPageAndFetch();
  }

  public setPeriodFilter(period: PeriodFilter) {
    this.periodFilter.set(period);
    this.resetPageAndFetch();
  }

  public statusFilterLabel(): string {
    const status = this.statusFilter();
    return status
      ? this._transloco.translate(`transactions.statusChip.${status}`)
      : this._transloco.translate('transactions.filterAll');
  }

  public typeFilterLabel(): string {
    const type = this.typeFilter();
    return type
      ? this._transloco.translate(`transactions.${type}`)
      : this._transloco.translate('transactions.filterAll');
  }

  public programFilterLabel(): string {
    return (
      this.programFilter()?.displayName ??
      this._transloco.translate('transactions.filterAllPrograms')
    );
  }

  public periodFilterLabel(): string {
    const period = this.periodFilter();
    return period
      ? this._transloco.translate(`transactions.period.${period}`)
      : this._transloco.translate('transactions.filterAll');
  }

  public acceptTransaction(transaction: TransactionDto, event: Event) {
    event.stopPropagation();
    this.loading = true;
    this._transactions.acceptTransaction(transaction.id).subscribe({
      next: () => {
        this.loading = false;
        this.refreshAfterRowRemoved();
        this._notification.success(
          this._transloco.translate('transactions.acceptTransactionSuccess')
        );
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  public openCancelModal(transaction: TransactionDto, event: Event) {
    event.stopPropagation();
    this.selectedTransaction = transaction;
    this.cancelTransactionMessage = null;
    this.cancelTransactionModal.openModal(event);
  }

  public cancelTransaction() {
    this.loading = true;
    this._transactions
      .cancelTransaction(this.selectedTransaction.id, this.cancelTransactionMessage)
      .subscribe({
        next: () => {
          this.loading = false;
          this.refreshAfterRowRemoved();
          this._notification.success(
            this._transloco.translate('transactions.cancelTransactionSuccess')
          );
          this.cancelTransactionMessage = null;
          this.selectedTransaction = null;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  /** Handle page event */
  public onPageChange(event: PageEvent) {
    this.getTransactions();
  }

  public previousPage() {
    if (this.paginator?.hasPreviousPage()) this.paginator.previousPage();
  }

  public nextPage() {
    if (this.paginator?.hasNextPage()) this.paginator.nextPage();
  }

  public hasPreviousPage(): boolean {
    return this.paginator?.hasPreviousPage() ?? false;
  }

  public hasNextPage(): boolean {
    return this.paginator?.hasNextPage() ?? false;
  }

  public rangeFrom(): number {
    if (this.totalElements() === 0) return 0;
    return (this.paginator?.pageIndex ?? 0) * this.pageSize + 1;
  }

  public rangeTo(): number {
    return Math.min(
      this.totalElements(),
      ((this.paginator?.pageIndex ?? 0) + 1) * this.pageSize
    );
  }

  /** Row click (buttons stop propagation) → transaction detail. */
  public openDetails(transaction: TransactionDto, event?: Event) {
    event?.stopPropagation();
    this._router.navigate(['/transactions', transaction.id], {
      state: { transaction },
    });
  }

  public formatCreated(transaction: TransactionDto): string {
    return relativeDateTime(transaction.created, this._transloco);
  }

  public isPending(transaction: TransactionDto): boolean {
    return (
      transaction.status === 'VALIDATION_PENDING' && !isLoyaltyEnded(transaction)
    );
  }

  /** Approved / initialized rows of active members can still be cancelled. */
  public isCancellable(transaction: TransactionDto): boolean {
    return (
      (transaction.status === 'SUCCESS' || transaction.status === 'INITIALIZED') &&
      !isLoyaltyEnded(transaction)
    );
  }

  /** Points are shown as "+N" only for rows that carry (or will carry) points. */
  public showsPoints(transaction: TransactionDto): boolean {
    return (
      transaction.points != null &&
      transaction.status !== 'DECLINED' &&
      transaction.status !== 'ERROR'
    );
  }

  private resetPageAndFetch() {
    if (this.paginator) this.paginator.pageIndex = 0;
    this.getTransactions();
  }

  /** Load the company's loyalty programs for the Program filter pill. */
  private getLoyaltyPrograms() {
    this._loyaltyPrograms.getCompanyLoyaltyPrograms().subscribe({
      next: (programs) => this.programList.set(programs ?? []),
    });
  }

  /** Today's calendar date in Europe/Zagreb as yyyy-MM-dd. */
  private zagrebToday(): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Zagreb',
    }).format(new Date());
  }

  /** Subtract `days` calendar days from a yyyy-MM-dd date string. */
  private isoDaysBefore(isoDate: string, days: number): string {
    const date = new Date(`${isoDate}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() - days);
    return date.toISOString().slice(0, 10);
  }

  /** Inclusive dateFrom/dateTo range for the selected period (Europe/Zagreb days). */
  private periodRange(): { dateFrom?: string; dateTo?: string } {
    const period = this.periodFilter();
    if (!period) return {};
    const today = this.zagrebToday();
    switch (period) {
      case 'TODAY':
        return { dateFrom: today, dateTo: today };
      case 'LAST_7':
        return { dateFrom: this.isoDaysBefore(today, 6), dateTo: today };
      case 'LAST_30':
        return { dateFrom: this.isoDaysBefore(today, 29), dateTo: today };
    }
  }
}
